import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cupSvg from '../../../assets/images/icons/cup.svg'
import settingsSvg from '../../../assets/images/icons/setting-3.svg'
import walletSvg from '../../../assets/images/icons/wallet-minus.svg'
import { useAnal, useNavigation } from '../../../hooks'
import { userInfoAtom } from '../../../store/atoms'
import type { RouterOutputType } from '../../../types'
import { ECurrency } from '../../../types/enums'
import { fetchWalletBalance } from '../../../utils'
import { Avatar, Button, Icon, Tooltip } from '../../atoms'
import { TooltipContent, TooltipTrigger } from '../../atoms/Tooltip'
import { Money, NotificationBadge } from '../../molecules'
import { SupportButton } from '..'
import {
  avatarBlock,
  balanceCell,
  cellLabel,
  connectWalletButton,
  iconStyle,
  iconsSection,
  nameStyle,
  profileContainer,
  profileInfo,
  rightIconsSection,
  supportButton,
} from './index.css'

export const ProfileBlock = (props: {
  data: RouterOutputType['user']['getUser']
}) => {
  const { t } = useTranslation()
  const { logEvent } = useAnal()
  const { goToPage } = useNavigation()
  const [tonConnectUi] = useTonConnectUI()
  const userTonAddress = useTonAddress()
  const userTonFriendlyAddress = useTonAddress(true)
  const [userInfo, setUserInfo] = useAtom(userInfoAtom)
  const [coinsBalance, setCoinsBalance] = useState<number>()
  const [tonBalance, setTonBalance] = useState<number>()

  useEffect(() => {
    if (!props.data) {
      return
    }
    if (props.data.adData) {
      const searchParams = new URLSearchParams(window.location.search)
      const searchParamsObject = Object.fromEntries(searchParams)
      logEvent('user_registered', {
        user_id: props.data.id,
        utm_source: props.data.adData?.sourceOrigin || undefined,
        campaign_id: props.data.adData?.campaignId || undefined,
        creative_id: props.data.adData?.creativeId || undefined,
        ...searchParamsObject,
      })
    }
    const newUserInfo =
      userInfo ||
      ({
        id: props.data.id,
        name: props.data.name,
      } satisfies typeof userInfoAtom.init)
    if (props.data.coinBalance) {
      setCoinsBalance(Number(props.data.coinBalance))
    }
    setUserInfo(newUserInfo)
  }, [props.data])

  useEffect(() => {
    if (!userInfo || !userTonAddress) {
      return
    }
    logEvent('wallet_connected')
    // TODO: useWalletBalance()
    void fetchWalletBalance(userTonAddress)
      .then((balance) => {
        setTonBalance(balance)
      })
      .catch(console.error)
  }, [userInfo?.id, userTonAddress])

  const onClickReward = () => {
    goToPage('rewards')
  }

  const onClickSettings = () => {
    goToPage('settings')
  }

  if (!props.data) return null

  return (
    <div className={profileContainer}>
      <div className={profileInfo}>
        <div className={avatarBlock}>
          <Avatar
            avatar={props.data.avatar}
            faceDirection='right'
          />
        </div>
        <div className={nameStyle}>{props.data.name}</div>
      </div>

      <div className={iconsSection}>
        <div>
          <SupportButton className={supportButton} />
        </div>
        <div className={rightIconsSection}>
          <NotificationBadge count={props.data.rewardableQuests}>
            <Icon
              src={cupSvg}
              className={iconStyle}
              aria-label={t('profileBlock.alt.menuRewards')}
              analytic={{
                name: 'menu',
                params: {
                  menu: 'rewards',
                },
              }}
              onClick={onClickReward}
            />
          </NotificationBadge>
          <Icon
            src={settingsSvg}
            className={iconStyle}
            aria-label={t('profileBlock.alt.menuSettings')}
            analytic={{
              name: 'menu',
              params: {
                menu: 'settings',
              },
            }}
            onClick={onClickSettings}
          />
        </div>
      </div>

      {userTonAddress !== '' ? (
        <div className={balanceCell}>
          <span className={cellLabel}>
            {t('profileBlock.label.tonBalance')}
          </span>
          <span>
            {tonBalance !== undefined ? (
              <Tooltip placement='bottom'>
                <TooltipTrigger>
                  <Money
                    inBigBlinds={false}
                    withIcon={true}
                    currency={ECurrency.TON}
                  >
                    {tonBalance}
                  </Money>
                </TooltipTrigger>
                <TooltipContent>
                  {userTonFriendlyAddress.slice(0, 8)}...
                  {userTonFriendlyAddress.slice(-8)}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </span>
        </div>
      ) : (
        <Button
          variant='primary'
          className={connectWalletButton}
          analytic={{
            name: 'connect_wallet',
          }}
          onClick={() => {
            void tonConnectUi.openModal()
          }}
        >
          <span>{t('profileBlock.button.connectWallet')}</span>
          <img
            src={walletSvg}
            alt=''
          />
        </Button>
      )}

      <div className={balanceCell}>
        <span className={cellLabel}>{t('profileBlock.label.coinBalance')}</span>
        <span>
          {coinsBalance ? (
            <Money
              inBigBlinds={false}
              withIcon={true}
              currency={ECurrency.COIN}
            >
              {coinsBalance}
            </Money>
          ) : null}
        </span>
      </div>
    </div>
  )
}
